/**
 * previewVerdict — what the file viewer's header offers (docs/PLAN-prest0n-edit-20261011.md C1): the server's verdict
 * for this person and this file (prest0nThreadAssets `verdict`, resolveAttachment.ts requestAttachmentVerdict), asked
 * once per open and kept a short while, and the header's actions read from it.
 *
 * A viewer opens with today's rules and takes the verdict's when it arrives, so nothing waits on it; a file the server
 * knows nothing of keeps today's rules. The server decides each action again when it is taken. The header's actions
 * the platform has not built yet are named here and stay off until their steps land.
 *
 * The lever is `REACT_APP_FILE_PREVIEW_ACTIONS`, a list of workspace ids where `*` admits every resolved workspace. Off
 * when absent; deploy.yml ships it as `*` (`vars.FILE_PREVIEW_ACTIONS` narrows or, naming no workspace, turns it off).
 * Off, the viewer asks nothing and keeps today's header.
 *
 * ON THE FIRST DOWNLOAD (the viewer's door, filePreviewDoor.tsx, asks as a file opens, while the viewer's own chunk
 * is still on its way); the viewers read it from here.
 */
import { useEffect, useState } from 'react';
import { buildEnv } from '../../config/buildEnv';
import { getActiveWorkspaceAccount } from '../../services/activeWorkspace';
import {
  requestAttachmentVerdict,
  type AttachmentResolveContext,
  type AttachmentVerdictAnswer,
} from '../../components/FilePreview/resolveAttachment';
import type { SerializedAttachment } from '../../components/GlobalChatSystem/types';

export const FILE_PREVIEW_ACTIONS_ENV = 'REACT_APP_FILE_PREVIEW_ACTIONS';

/** Whether this workspace's viewer takes its header from the server's verdict. */
export function filePreviewActionsShow(
  accountId: string | null | undefined,
  env: Record<string, string | undefined> = buildEnv(),
): boolean {
  const account = String(accountId ?? '').trim();
  if (!account) return false;
  const listed = String(env[FILE_PREVIEW_ACTIONS_ENV] ?? '').split(',').map((s) => s.trim());
  return listed.includes('*') || listed.includes(account);
}

/**
 * The header's actions, in the order the record names them. Only Download has a rule yet; each other waits for its own
 * step and brings its rule with it: Prest0n edit (C2), Edit (D), History (C5), Share (B2), Listen (the read-aloud
 * record's SG3d) and Sign (the signing record's SG3c).
 */
export const PREVIEW_ACTIONS = ['prest0n-edit', 'edit', 'history', 'share', 'download', 'listen', 'sign'] as const;
export type PreviewAction = (typeof PREVIEW_ACTIONS)[number];

/** Where the verdict stands for one open file. */
export type PreviewVerdictState = { state: 'off' } | { state: 'asking' } | AttachmentVerdictAnswer;

/**
 * The header's actions (pure): the verdict's where it has one, today's rules where it does not. `today` is what the
 * viewer can do on its own (a download needs an address or the file's bytes). A file the server says this person may
 * not see offers nothing.
 */
export function previewHeaderActions(verdict: PreviewVerdictState, today: { download: boolean }): Record<PreviewAction, boolean> {
  const out = Object.fromEntries(PREVIEW_ACTIONS.map((a) => [a, false])) as Record<PreviewAction, boolean>;
  if (verdict.state === 'ok') out.download = today.download && verdict.verdict.download;
  else if (verdict.state !== 'missing') out.download = today.download;
  return out;
}

/** How long one verdict is kept: an open soon after reads it again for free; access can change, so not for long. */
export const PREVIEW_VERDICT_KEPT_MS = 2 * 60_000;
const KEPT_MAX = 50;
const kept = new Map<string, { atMs: number; answer: Promise<AttachmentVerdictAnswer> }>();

/**
 * Ask for the verdict of the file a viewer is opening, or take the one asked a moment ago. Null when this workspace's
 * viewer keeps today's header. A failed answer is never kept: the next open asks again.
 */
export function primePreviewVerdict(
  serialized: SerializedAttachment | null | undefined,
  context: AttachmentResolveContext | null | undefined,
  nowMs: number = Date.now(),
): Promise<AttachmentVerdictAnswer> | null {
  const account = getActiveWorkspaceAccount();
  if (!filePreviewActionsShow(account)) return null;
  const key = `${account}|${context?.threadId ?? ''}|${serialized?.storagePath ?? ''}`;
  const had = kept.get(key);
  if (had && nowMs - had.atMs < PREVIEW_VERDICT_KEPT_MS) return had.answer;
  const answer = requestAttachmentVerdict(serialized, context);
  kept.delete(key);
  kept.set(key, { atMs: nowMs, answer });
  if (kept.size > KEPT_MAX) kept.delete(kept.keys().next().value as string);
  void answer.then((a) => {
    if (a.state === 'failed' && kept.get(key)?.answer === answer) kept.delete(key);
  });
  return answer;
}

/** The verdict for the file a viewer shows: `asking` until it arrives, then the answer; `off` where the lever is. */
export function usePreviewVerdict(
  serialized: SerializedAttachment | null | undefined,
  context: AttachmentResolveContext | null | undefined,
): PreviewVerdictState {
  const path = serialized?.storagePath ?? '';
  const thread = context?.threadId ?? '';
  const [state, setState] = useState<PreviewVerdictState>({ state: 'asking' });
  useEffect(() => {
    const answer = primePreviewVerdict(serialized, context);
    if (!answer) {
      setState({ state: 'off' });
      return undefined;
    }
    let live = true;
    setState({ state: 'asking' });
    void answer.then((a) => { if (live) setState(a); });
    return () => { live = false; };
    // The file and the thread it was opened from name the verdict; the rest of each object does not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, thread]);
  return state;
}

/** Tests only: forget every kept verdict. */
export function resetPreviewVerdictsForTests(): void {
  kept.clear();
}
